import React from 'react';

/**
 * eis ekosis LMS brand mark for the student / teacher / parent app.
 *
 * Two lockups ship, and the right one depends on how much vertical room the slot
 * gives — not on taste:
 *
 *   full   "eis ekosis" wordmark stacked above LMS. The wordmark band is only
 *          ~18% of the lockup height, so under ~56px it turns into an unreadable
 *          smudge. Use it where there is room (login, register, access-denied).
 *   mark   LMS alone. The letters fill the full height, so it stays sharp in the
 *          small slots (sidebar, top bar).
 *
 * The eis ekosis rebrand only touched `full`. Both lockups place the gradient LMS
 * in an identical 222px band — the old file had the wordmark in rows 0-70 and LMS
 * in 105-326, the new one 0-60 and 107-328 — so `mark`, which is that band alone,
 * carries no wordmark and needed no new asset. Swapping it would be a no-op file
 * churn, not a rebrand.
 *
 * Before this component the five logo slots were five hand-written <img> tags
 * pointing at the same file; the threshold lives here so they cannot drift.
 *
 * `tone` picks the ink — 'color' on light surfaces, 'white' on dark or gradient
 * ones. Both lockups exist in both inks.
 */

/** Below this rendered height the "eis ekosis" wordmark stops being legible. */
const FULL_LOCKUP_MIN_HEIGHT = 56;

export type BrandLogoVariant = 'full' | 'mark';
export type BrandLogoTone = 'color' | 'white';

const SRC: Record<BrandLogoVariant, Record<BrandLogoTone, string>> = {
  full: { color: '/eisekosis-lms-logo.png', white: '/eisekosis-lms-logo-white.png' },
  mark: { color: '/bryekosis-lms-mark.png', white: '/bryekosis-lms-mark-white.png' },
};

interface Props {
  /** Rendered height in px. Drives the variant unless `variant` is given. */
  height?: number;
  /** Tailwind height classes for responsive slots, e.g. "h-[36px] xl:h-[44px]".
   *  When set, pass `variant` too — the class can't be measured here. */
  className?: string;
  tone?: BrandLogoTone;
  variant?: BrandLogoVariant;
}

const BrandLogo: React.FC<Props> = ({ height, className, tone = 'color', variant }) => {
  const resolved: BrandLogoVariant =
    variant ?? (height != null && height >= FULL_LOCKUP_MIN_HEIGHT ? 'full' : 'mark');
  return (
    <img
      src={SRC[resolved][tone]}
      alt="eis ekosis LMS"
      className={className ?? 'object-contain select-none'}
      style={height != null && !className ? { height, width: 'auto' } : undefined}
      draggable={false}
    />
  );
};

export default BrandLogo;
